/**
 * WizardStepTemplate - Passo 1: Selecionar contrato publicado (status=production)
 * 
 * RELEASE: v2.0.0 - 2026-02-19
 * - Migrado de templates brutos para published_contracts com status=production
 * - Filtra automaticamente por workspace do deal quando informado
 * - Exibe informações de provider e versão do template
 */
import React, { useState } from 'react';
import { FileSignature, Search, CheckCircle2, Building2, Tag } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { PublishedContract } from '@/types/contracts';

interface WizardStepTemplateProps {
  /** Lista de contratos publicados com status=production */
  contracts: PublishedContract[];
  loading: boolean;
  onSelect: (contract: PublishedContract) => void;
  /** Workspace ID do deal atual para filtro automático */
  dealWorkspaceId?: string | null;
}

export function WizardStepTemplate({ contracts, loading, onSelect, dealWorkspaceId }: WizardStepTemplateProps) {
  const [search, setSearch] = useState('');

  // Filtra contratos por busca e workspace do deal
  const filtered = contracts.filter((c) => {
    // Filtro de texto
    const matchSearch =
      !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.description || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.contract_templates?.name || '').toLowerCase().includes(search.toLowerCase());

    // Filtro de workspace: se workspace_ids for vazio = aparece em todos
    // Se preenchido, só aparece se o workspace do deal estiver na lista
    const matchWorkspace =
      !dealWorkspaceId ||
      c.workspace_ids.length === 0 ||
      c.workspace_ids.includes(dealWorkspaceId);

    return matchSearch && matchWorkspace;
  });

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Selecione o contrato a ser gerado. Apenas contratos em <strong>Produção</strong> são exibidos.
      </p>

      {/* Busca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar contrato..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-9 text-sm"
        />
      </div>

      {/* Lista de contratos publicados */}
      {filtered.length === 0 ? (
        <div className="py-12 text-center">
          <FileSignature className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            {contracts.length === 0
              ? 'Nenhum contrato em produção cadastrado'
              : 'Nenhum contrato encontrado'}
          </p>
          {contracts.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              Acesse CRM → Contratos para criar e publicar um contrato
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
          {filtered.map((contract) => {
            const providerName =
              contract.tenant_signature_configs?.signature_providers?.display_name ||
              contract.tenant_signature_configs?.signature_providers?.name;
            const templateName = contract.contract_templates?.name;
            // Versão: exibe número real da versão fixada ou calcula o máximo disponível no template
            const versionLabel = (() => {
              // Versão fixada: usa o version_number da versão pinada
              if (contract.template_version_id && contract.contract_template_versions?.version_number != null) {
                const n = contract.contract_template_versions.version_number;
                return `v1.${n - 1} (fixada)`;
              }
              // "Mais recente": calcula o max version_number das versões do template (join aninhado)
              const versions = contract.contract_templates?.contract_template_versions;
              if (versions && versions.length > 0) {
                const maxN = Math.max(...versions.map((v: { version_number: number }) => v.version_number));
                return `v1.${maxN} (atual)`;
              }
              return 'v1.0 (atual)';
            })();

            return (
              <button
                key={contract.id}
                onClick={() => onSelect(contract)}
                className={cn(
                  'w-full text-left p-3 rounded-lg border transition-colors',
                  'hover:bg-primary/5 hover:border-primary/30 group'
                )}
              >
                <div className="flex items-start gap-3">
                  {/* Ícone */}
                  <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-primary/20 transition-colors">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Nome + badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{contract.name}</span>
                      {contract.workspace_ids.length === 0 && (
                        <Badge variant="secondary" className="text-[10px] h-4">Todos workspaces</Badge>
                      )}
                    </div>

                    {/* Metadados: versão + modelo + provider */}
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {/* Versão do contrato publicado */}
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        {versionLabel}
                      </span>
                      {templateName && (
                        <span className="text-xs text-muted-foreground">
                          Modelo: {templateName}
                        </span>
                      )}
                      {providerName && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {providerName}
                        </span>
                      )}
                      {contract.default_signers.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {contract.default_signers.length} signatário(s) padrão
                        </span>
                      )}
                    </div>

                    {/* Descrição */}
                    {contract.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {contract.description}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
